// Swapping the session list in place when a date is picked.
//
// The centre's sessions take a second or two to come back, and until they do
// the visitor has nothing to tell them their click landed. So the date they
// picked is highlighted straight away, the old sessions go, and a pulsing
// placeholder holds the space until the new ones arrive.
//
// Only the most recent pick may render: someone tapping along the date strip
// fires several requests, and they do not necessarily come back in order.

const SKELETON_GROUPS = [4, 3, 5];

let inFlight = null;
let latest = 0;

function abortController() {
  return typeof AbortController === 'undefined' ? null : new AbortController();
}

function announce(status, message) {
  if (status) status.textContent = message;
}

function skeletonGroup(rows) {
  const bars = '<span class="timetable-skeleton__bar timetable-skeleton__bar--row"></span>'.repeat(rows);
  return (
    '<div class="weekly-timetable__category timetable-skeleton__category">' +
      '<div class="weekly-timetable__category-header">' +
        '<span class="timetable-skeleton__bar timetable-skeleton__bar--heading"></span>' +
      '</div>' +
      bars +
    '</div>'
  );
}

export function skeletonMarkup() {
  return (
    '<div class="weekly-timetable wrapper panel timetable-skeleton" aria-hidden="true">' +
      `<div class="weekly-timetable__sessions">${SKELETON_GROUPS.map(skeletonGroup).join('')}</div>` +
    '</div>'
  );
}

export function errorMarkup() {
  return (
    '<div class="weekly-timetable wrapper panel">' +
      '<div class="weekly-timetable__sessions">' +
        '<div class="weekly-timetable__empty weekly-timetable__error" role="alert">' +
          '<p class="weekly-timetable__empty-title">We could not load the sessions for this date</p>' +
          '<p class="weekly-timetable__empty-subtitle">Please check your connection and try again.</p>' +
          '<button type="button" class="weekly-timetable__retry" data-timetable-retry>Try again</button>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

function ensureVisible(container) {
  const { top } = container.getBoundingClientRect();
  if (top >= 0 && top < window.innerHeight) return;
  container.scrollIntoView({ block: 'nearest' });
}

function sessionCount(container) {
  return [...container.querySelectorAll('.weekly-timetable__row[data-category]')]
    .filter(row => row.style.display !== 'none').length;
}

function loadedMessage(container, label) {
  const count = sessionCount(container);
  if (count === 0) return `No sessions found for ${label}`;
  return `${count} ${count === 1 ? 'session' : 'sessions'} loaded for ${label}`;
}

function render(options, html) {
  const { container, status, label, onLoaded } = options;
  container.innerHTML = html;
  container.setAttribute('aria-busy', 'false');
  if (onLoaded) onLoaded();
  announce(status, loadedMessage(container, label));
}

function fail(options) {
  const { container, status, label } = options;
  container.innerHTML = errorMarkup();
  container.setAttribute('aria-busy', 'false');
  container.querySelector('[data-timetable-retry]')
    ?.addEventListener('click', () => loadTimetableDate(options)); // eslint-disable-line no-use-before-define
  announce(status, `Sessions for ${label} could not be loaded`);
}

function startSkeleton(options) {
  const { container, status, label } = options;
  container.setAttribute('aria-busy', 'true');
  container.innerHTML = skeletonMarkup();
  ensureVisible(container);
  announce(status, `Loading sessions for ${label}`);
}

export default function loadTimetableDate(options) {
  const { container, url } = options;
  if (!container) return Promise.resolve();

  const token = ++latest;
  if (inFlight) inFlight.abort();
  inFlight = abortController();
  startSkeleton(options);

  return fetch(url, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    signal: inFlight ? inFlight.signal : undefined,
  })
    .then(response => {
      if (!response.ok) throw new Error(`Timetable request failed: ${response.status}`);
      return response.text();
    })
    .then(html => { if (token === latest) render(options, html); })
    .catch(error => { if (error.name !== 'AbortError' && token === latest) fail(options); });
}
